import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Components
import { Loader, BuildersDropdown } from '../components';
import AllApprovedProjects from './AllApprovedProjects';

// Helpers
import { filterProjects } from '../components/helpers';

// vars
const adminFilters = {
  status: 'Approved',
  builder: 0,
  year: ''
};

// Keep outside of function, or will get overwritten at re-render.
const _PROJECTSCACHE = [];

const AdminPayments = () => {
  const [projects, setProjects] = useState([]);
  const [builders, setBuilders] = useState([]);
  const [selectedProjectIDs, setSelectedProjectIDs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ ...adminFilters });
  const [mainNotice, setMainNotice] = useState({ type: '', message: '' });
  const [builder, setBuilder] = useState({});
  const [updatedProjects, setUpdatedProjects] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const applicableYears = window.wlConfig.applicableYears;

  const parseAndSetBuilders = () => {
    if (!_PROJECTSCACHE.length) {
      setBuilders([]);
      return;
    }

    const builders = _PROJECTSCACHE.map(({ builder }) => builder);
    const reducedBuilders = [];

    builders.filter((builder, index) => {
      const builderID = builder.ID;

      if (!reducedBuilders.some((builder) => builder.ID === builderID)) {
        reducedBuilders.push(builder);
      }
    });

    setBuilders(reducedBuilders);
  };

  const setLocalStorageFilter = (event) => {
    event.preventDefault();

    localStorage.setItem(
      'adminFilters',
      JSON.stringify({
        status: '',
        builder: filter.builder,
        state: ''
      })
    );

    window.location = event.currentTarget.href;
  };

  const handleFilterChange = (property, value) => {
    const updatedFilter = { ...filter };
    updatedFilter[property] = value;
    setFilter(updatedFilter);

    const builder = builders.find(
      (builder) => builder.ID == updatedFilter.builder
    );

    setBuilder(builder);
    setSelectedProjectIDs([]);
  };

  const handleSelectProject = (pid) => {
    if (!pid) return;

    let newArr = [...selectedProjectIDs];

    // if id is already present in array, remove from array.
    if (newArr.includes(pid)) {
      newArr = selectedProjectIDs.filter((id) => id !== pid);
    } else {
      // else add.
      newArr.push(pid);
    }

    setSelectedProjectIDs(newArr);
  };

  const handleSelectAllProjects = (projects) => {
    if (!projects.length) return;

    const pids = projects.map((project) => project.pid);
    let newArr = [...selectedProjectIDs];

    const allProjectIDsPresent =
      newArr.length > 0 && pids.every((pid) => newArr.includes(pid));

    // If all Project IDs are present, bulk remove all of them.
    newArr = allProjectIDsPresent ? [] : [...pids];

    setSelectedProjectIDs(newArr);
  };

  const sendChunkedProjectIdsForSubmittal = (projectIDs, builderID, actionType, counter, chunkedPids, successfullyUpdated) => {
    setSubmitting(true);
    const formData = new FormData();
    formData.append('action', 'wl_pip_approve_for_payment');
    formData.append('type', actionType);
    formData.append('pids', projectIDs);
    formData.append('bid', builderID);

    axios({
      method: 'post',
      url: window.wlPipConfig.ajaxUrl,
      data: formData
    })
      .then(( { data: { success, data } }) => {
        if (!success) {
          setMainNotice({
            type: 'error',
            message: data.message
          });
  
          setLoading(false);
          setSubmitting(false);
          setUpdatedProjects([]);
        } else {
          const { updated_projects, message, projects } = data;
          successfullyUpdated = [ ...successfullyUpdated, ...updated_projects];
          setUpdatedProjects(successfullyUpdated);
  
          // keep sending chunks until we're done
          if (counter < (chunkedPids.length - 1)) {
            counter ++;
            sendChunkedProjectIdsForSubmittal(chunkedPids[counter], builderID, actionType, counter, chunkedPids, successfullyUpdated);
          } else {
            let noticeMessage = message;
            successfullyUpdated.map(({ address, permalink }) => {
              noticeMessage += `<p class="u--margin-10">- <a href="${permalink}" class="u--color-black u--font-weight-medium u--font-size-md">${address}</a></p>`;
            })
  
            noticeMessage += '</ul>';
    
            setMainNotice({
              type: success ? 'success' : 'error',
              message: noticeMessage
            });
    
            _PROJECTSCACHE.length = 0;
            _PROJECTSCACHE.push(...projects);
            setProjects(filterProjects(_PROJECTSCACHE, filter));
    
            setLoading(false);
            setSubmitting(false);
            setUpdatedProjects([]);
            setSelectedProjectIDs([]);
          }
        }

      })
      .catch((error) => {
        setMainNotice({
          type: 'error',
          message: error.response.data
        });

        setLoading(false);
        setSubmitting(false);
        setUpdatedProjects([]);
      });
  }

  const handleSendForPayment = (pids, actionType) => {
    if (!pids.length) return;
    let builderID = filter.builder;

    // If `builder` in filter obj is falsey, user has not selected a builder in dropdown menu, but has selected projects by clicking on cards (possibly by different builders).
    // Use different function instead.
    if (!builderID) {
      if (actionType === 'submit') {
        return handleSendForPaymentWithoutBuilderInfo();
      }

      // We can't create a PDF for more than 1 builder. Check selected project IDs, and see what's up.
      if (actionType === 'download') {
        if (pids.length === 1) {
          const project = _PROJECTSCACHE.find(({ pid }) => pid === pids[0]);
          builderID = project.builder.ID;
        } else {
          const builderArray = [];

          pids.map((projectID) => {
            const project = _PROJECTSCACHE.find(({ pid }) => pid === projectID);

            if (!project) return false;

            builderArray.push(project.builder);
          });

          // If the selected projects are by more than 1 builder, bail.
          if (builderArray.length > 1) {
            return alert(
              'Can\'t generate a PDF for multiple builders. Make sure to only select projects by 1 builder (use the "-- Builder --" dropdown to filter by 1 builder).'
            );
          }
        }
      }
    }

    setLoading(true);
    setMainNotice({ type: '', message: '' });

    // If sending for submittal, chunk PIDs to prevent server time outs (batches of 5)
    if (actionType === 'submit') {
      let counter = 0;
      // chunk em up
      const chunkedPids = pids.reduce((chunked, pid, index) => { 
        const chunkIndex = Math.floor(index / 5);
  
        if(!chunked[chunkIndex]) {
          chunked[chunkIndex] = []; // start a new chunk
        }
  
        chunked[chunkIndex].push(pid);
  
        return chunked;
      }, [])

      sendChunkedProjectIdsForSubmittal(chunkedPids[0], builderID, actionType, counter, chunkedPids, []);
    } else {
      const formData = new FormData();
      formData.append('action', 'wl_pip_approve_for_payment');
      formData.append('type', actionType);
      formData.append('pids', pids);
      formData.append('bid', builderID);
  
      axios({
        method: 'post',
        url: window.wlPipConfig.ajaxUrl,
        data: formData
      })
        .then(({ data: { success, data } }) => {
            const { pdfData } = data;
            const docTitle = 'PCIP Payment PDF';
            const pdfObject = `<object width="100%" height="100%" data="data:application/pdf;base64,${pdfData}" type="application/pdf" class="internal"><embed src="data:application/pdf;base64,${pdfData}" type="application/pdf" /></object>`;
  
            let newPDFWindow = window.open('#', '_blank');
            newPDFWindow.document.write(`<html>
                  <head>
                    <title>${docTitle}</title>
                  </head>
                  <body style="margin-top: 0px; margin-left: 0px; margin-right: 0px; margin-bottom: 0px;">
                    ${pdfObject}
                  </body>
                </html>`);
  
            setMainNotice({
              type: success ? 'success' : 'error',
              message: data.message
            });
  
          setLoading(false);
        })
        .catch((error) => {
          setMainNotice({
            type: 'error',
            message: error.response.data
          });
  
          setLoading(false);
        });
      }
  };

  const handleSendForPaymentWithoutBuilderInfo = () => {
    if (!selectedProjectIDs.length) return;

    const projectIDsByBuilder = {};

    // Get project data for selected project IDs, and sort by builder.
    selectedProjectIDs.map((pid) => {
      const project = _PROJECTSCACHE.find((project) => project.pid === pid);

      if (!project) return;

      const builderID = project.builder.ID.toString();

      if (builderID in projectIDsByBuilder) {
        projectIDsByBuilder[builderID].pids.push(pid);
      } else {
        const builderObject = {
          pids: [pid]
        };

        projectIDsByBuilder[builderID] = builderObject;
      }
    });

    setLoading(true);
    const builderIDs = Object.keys(projectIDsByBuilder);

    if (
      !confirm(
        `This will send ${selectedProjectIDs.length} projects ${
          Object.keys(projectIDsByBuilder).length > 1
            ? `by ${
                Object.keys(projectIDsByBuilder).length
              } different builders `
            : ''
        }for approval, are you sure?`
      )
    ) {
      setLoading(false);
      return;
    }

    setMainNotice({ type: '', message: '' });

    // "Chunk up" ProjectIDsByBuilder by sending one builder at a time
    let counter = 0;

    const submitChunkedPidsByBuilder = (projectIDsByBuilder, counter, successfullyUpdated, builderIDs) => {
      setBulkSubmitting(true);
      const formData = new FormData();
      formData.append('action', 'wl_pip_approve_for_payment');
      formData.append('type', 'submit');
      formData.append('pids', projectIDsByBuilder[builderIDs[counter]].pids);
      formData.append('bid', builderIDs[counter]);
  
      axios({
        method: 'post',
        url: window.wlPipConfig.ajaxUrl,
        data: formData
      })
        .then(({ data: { success, data } }) => {
          const { updated_projects, message, projects } = data;
          successfullyUpdated = [ ...successfullyUpdated, ...updated_projects];
          setUpdatedProjects(successfullyUpdated);

          // keep sending chunks until we're done
          if (counter < (builderIDs.length - 1)) {
            counter ++;
            submitChunkedPidsByBuilder(projectIDsByBuilder, counter, successfullyUpdated, builderIDs);
          } else {
            let noticeMessage = message;

            if (success) {
              successfullyUpdated.map(({ address, permalink }) => {
                noticeMessage += `<p class="u--margin-10">- <a href="${permalink}" class="u--color-black u--font-weight-medium u--font-size-md">${address}</a></p>`;
              });
            }
    
            noticeMessage += '</ul>';
    
            setSelectedProjectIDs([]);
    
            _PROJECTSCACHE.length = 0;
            _PROJECTSCACHE.push(...projects);
            setProjects(filterProjects(_PROJECTSCACHE, filter));
    
            setMainNotice({
              type: success ? 'success' : 'error',
              message: noticeMessage
            });
            setLoading(false);
            setBulkSubmitting(false);
            setUpdatedProjects([]);
          }

        })
        .catch((error) => {
          setMainNotice({
            type: 'error',
            message: error.reponse.data
          });
  
          setLoading(false);
          setBulkSubmitting(false);
          setUpdatedProjects([]);
        });
      }

      submitChunkedPidsByBuilder(projectIDsByBuilder, counter, [], builderIDs);
  };

  const getProjects = () => {
    setLoading(true);

    const formData = new FormData();
    formData.append('action', 'wl_pip_admin_get_projects');
    formData.append('status', 'Approved');

    axios({
      method: 'post',
      url: window.wlPipConfig.ajaxUrl,
      data: formData
    })
      .then(({ data: { success, data } }) => {
        if (success) {
          _PROJECTSCACHE.length = 0;
          _PROJECTSCACHE.push(...data);
          setProjects(filterProjects(_PROJECTSCACHE, filter));
        } else {
          setProjects([]);
          setMainNotice({
            type: success ? 'success' : 'error',
            message: data.message
          });
        }

        setSelectedProjectIDs([]);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  // Update projects on filter change.
  useEffect(() => setProjects(filterProjects(_PROJECTSCACHE, filter, null)), [
    filter
  ]);

  // Update Builders dropdown on projects update.
  useEffect(() => parseAndSetBuilders(_PROJECTSCACHE), [projects]);

  // Init.
  useEffect(() => {
    if (_PROJECTSCACHE.length) return;

    getProjects();
  }, []);

  if (!projects.length && loading) {
    return (
      <>
        <div className="grid">
          <div className="grid__col-xs-12">
            <h1 className="u--color-blue-dark u--margin-top-0">Payments</h1>
          </div>
        </div>
        <Loader />
      </>
    );
  }

  return (
    <>
      <div className="grid">
        <div className="grid__col-xs-12">
          <h1 className="u--color-blue-dark u--margin-top-0">Payments</h1>
        </div>
      </div>
      <div className="grid grid--gutter-vertical grid--row-stretch">
        {loading && <Loader/>}

        <div className="grid__col-xs-12">
          <div className="form__select form__select--lg u--margin-bottom-0">
            <select
              className="form__input form__input--lg u--margin-bottom-20"
              disabled={loading === true}
              value={filter.year}
              onChange={(event) =>
                handleFilterChange('year', event.target.value)
              }
            >
              <option value="">-- All Years --</option>
              {applicableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div className="form__select form__select--lg u--margin-bottom-0">
            <BuildersDropdown
              builders={builders}
              disabled={loading === true}
              value={filter.builder}
              onChange={(event) =>
                handleFilterChange('builder', event.target.value)
              }
            />
          </div>
        </div>

        { submitting && (
          <div className="grid__col-xs-12">
            <div className="pip-progress-bar">
              <span style={{width: `${(updatedProjects.length / selectedProjectIDs.length) * 100}%`}}></span>
              <p><small>{updatedProjects.length} of {selectedProjectIDs.length} projects have been sent</small></p>
            </div>
          </div>
        )}

        {projects.length > 0 && filter.builder != 0 ? (
          <div className="grid__col-xs-12 u--padding-bottom-0">
            <button
              className="btn btn--xs btn--gold"
              onClick={() => handleSelectAllProjects(projects)}
            >
              {selectedProjectIDs.length === projects.length
                ? 'Deselect '
                : 'Select '}
              all projects
            </button>
            <ul style={{ listStyleType: 'none' }}>
              {projects.map(({ address, pid }, index) => (
                <li key={index}>
                  <label className="form__checkbox form__checkbox--sm u--margin-top-0">
                    <input
                      type="checkbox"
                      checked={
                        selectedProjectIDs !== undefined &&
                        selectedProjectIDs.includes(pid)
                      }
                      onChange={() => handleSelectProject(pid)}
                    />{' '}
                    {address}
                  </label>
                </li>
              ))}
            </ul>
          </div>
        ) : filter.builder != 0 ? (
          <div className="grid__col-xs-12 u--padding-bottom-0">
            <p className="u--font-size-md">
              Builder does not have any projects with an{' '}
              <strong>Approved</strong> status
              {filter.year ? ` for year ${filter.year}` : ''}.
              <br />
              <a
                href="/pcip/dashboard/"
                onClick={(event) => setLocalStorageFilter(event)}
              >
                View all{' '}
                {builder.first_name && builder.last_name
                  ? `of ${builder.first_name} ${builder.last_name}'s `
                  : "builder's "}
                projects on the Admin Dashboard.
              </a>
            </p>
          </div>
        ) : (
          <AllApprovedProjects
            projects={projects}
            selectedProjectIDs={selectedProjectIDs}
            handleSelectProject={handleSelectProject}
          />
        )}
        { bulkSubmitting && (
          <div className="grid__col-xs-12">
            <div className="pip-progress-bar">
              <span style={{width: `${(updatedProjects.length / selectedProjectIDs.length) * 100}%`}}></span>
              <p><small>{updatedProjects.length} of {selectedProjectIDs.length} projects have been sent</small></p>
            </div>
          </div>
        )}
        {mainNotice.type && mainNotice.message && (
          <div className="grid__col-xs-12 u--padding-top-0">
            <div
              className={`notice notice--${mainNotice.type}`}
              role="alert"
              dangerouslySetInnerHTML={{ __html: mainNotice.message }}
            />
          </div>
        )}
        <div className="grid__col-xs-12 u--padding-bottom-0">
          <button
            className="btn btn--white btn--sm u--margin-right-10 u--margin-bottom-10"
            disabled={selectedProjectIDs.length === 0 || loading === true}
            onClick={() => handleSendForPayment(selectedProjectIDs, 'download')}
            title="mererp"
          >
            <span className="btn__content">
              <span className="btn__text">Download payment information</span>
            </span>
          </button>
          <button
            className="btn btn--green btn--sm u--margin-bottom-10"
            disabled={selectedProjectIDs.length === 0 || loading === true}
            onClick={() => handleSendForPayment(selectedProjectIDs, 'submit')}
          >
            <span className="btn__content">
              <span className="btn__text">
                Send selected{' '}
                {selectedProjectIDs.length === 1 ? 'project' : 'projects'} for
                payment to Bill.com
              </span>
            </span>
          </button>
        </div>
      </div>
    </>
  );
};

export default AdminPayments;
